// Companion/ChatBlockScopePill.tsx
//
// The composer's question pill inside a QBank block (ORA-1285). It still says
// what Ora can see and still has its ✕ (ORA-662), and it also opens the menu
// that says what this chat covers: this question, or the whole block. One
// control on the pill that shows what Ora can see; the header stays as it is.

import { CaretDown, Question, Stack, X } from '@phosphor-icons/react';
import { useSelector, useStore } from 'react-redux';
import type { ChatBlockContext } from '@/chatContext/chatBlockThread';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { RootState } from '@/store/store';
import {
  isScopeChangeBlocked,
  switchToBlockScope,
  switchToQuestionScope,
} from './chatBlockScopeActions';
import { useChatBlockThreadDefault } from './useChatBlockThreadDefault';

interface ChatBlockScopePillProps {
  context: ChatBlockContext;
  /** `block` once the chat covers the whole block; anything else is per question. */
  isBlock: boolean;
  /** The ✕: Ora stops seeing the question, as on any context pill. */
  onRemove: () => void;
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-default';

const ScopeOption = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <span className="flex min-w-0 flex-col">
    <span className="font-heading font-medium">{title}</span>
    <span className="text-xs opacity-70">{description}</span>
  </span>
);

const ChatBlockScopePill = ({
  context,
  isBlock,
  onRemove,
}: ChatBlockScopePillProps) => {
  const store = useStore<RootState>();
  const isBlocked = useSelector(isScopeChangeBlocked);
  const { isOn: isDefaultOn, isSaving, save } = useChatBlockThreadDefault();
  const position = `Question ${context.index} of ${context.total}`;
  const covers = isBlock ? 'the whole block' : 'this question';
  const Icon = isBlock ? Stack : Question;

  const handleScopeChange = (value: string) => {
    if (value === 'block') void switchToBlockScope(store, 'pill');
    if (value === 'question') void switchToQuestionScope(store);
  };

  const handleAlwaysChange = async (next: boolean) => {
    const saved = await save(next, 'always');
    // Turning it on also means this block, now.
    if (saved && next && !isBlock) await switchToBlockScope(store, 'always');
  };

  return (
    <span className="inline-flex min-w-0 max-w-full">
      <Badge
        variant={isBlock ? 'chip-accent' : 'chip-highlight'}
        className="min-w-0 max-w-full"
        data-testid="chat-context-pill"
        data-scope={isBlock ? 'block' : 'question'}
        data-icon={isBlock ? 'stack' : 'question'}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`${position}. This chat covers ${covers}.`}
              data-testid="chat-block-scope-trigger"
              className={cn(
                'group inline-flex min-w-0 cursor-pointer items-center gap-1 rounded-full',
                FOCUS_RING
              )}
            >
              <Icon
                size={12}
                aria-hidden="true"
                className={cn(isBlock && 'text-green-600 dark:text-green-400')}
              />
              <span className="min-w-0 truncate">
                {isBlock ? `Block · ${position}` : position}
              </span>
              <CaretDown
                size={10}
                aria-hidden="true"
                className="shrink-0 transition-transform group-data-[state=open]:rotate-180"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            className="w-72"
            aria-label="What this chat covers"
          >
            <DropdownMenuLabel>This chat covers</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={isBlock ? 'block' : 'question'}
              onValueChange={handleScopeChange}
            >
              <DropdownMenuRadioItem value="question" disabled={isBlocked}>
                <ScopeOption
                  title="This question"
                  description="A new chat starts on each question."
                />
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="block" disabled={isBlocked}>
                <ScopeOption
                  title="The whole block"
                  description="Ora follows you to each question."
                />
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={isDefaultOn}
              disabled={isSaving || isBlocked}
              // The menu stays open: the student sees the switch take.
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={(next) => void handleAlwaysChange(next)}
            >
              <ScopeOption
                title="Always for my blocks"
                description="You can change this in Settings."
              />
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          aria-label="Remove question context"
          onClick={onRemove}
          className={cn(
            'ml-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
            'text-fg-secondary hover:text-fg-primary',
            FOCUS_RING
          )}
        >
          <X size={12} aria-hidden="true" />
        </button>
      </Badge>
    </span>
  );
};

export default ChatBlockScopePill;
